import { useEffect, useState } from 'react'
import {
  formatDateTime,
  formatRelativeFromNow,
  parseUserAgent,
} from '../../config/resources/utils'
import {
  ADMIN_CONFIGURABLE_METHODS,
  METHOD,
  normalizePensionMethod,
} from '../../config/settings/constants'
import { SUPPORT_CATEGORY_LABELS } from '../../config/settings/support'
import {
  getUserAuditEvents,
  getUserConfigHistory,
  getUserOpsAuthEvents,
  getUserOpsLogs,
  getUserOpsNotifications,
  getUserOpsSessions,
  getUserOpsTickets,
  revokeUserOpsSession,
} from '../../services/userOps'
import { getRolesCatalog } from '../../services/pension'
import { validatePensionValue } from '../PensionConfig/PensionConfigForm'
import Button from '../../components/Buttons/BaseButton'
import Input from '../../components/Inputs/Input'
import Spinner from '../../components/Spinners/Spinner'
import * as S from './PensionUser.styles'

const PROVIDER_LABELS = {
  google: 'Google',
  email: 'Email',
}

const AUTH_ACTION_LABELS = {
  login: 'Inicio de sesión',
  logout: 'Cierre de sesión',
  user_signedup: 'Alta de cuenta',
  token_revoked: 'Sesión revocada',
}

const AUDIT_ACTION_LABELS = {
  status_updated: 'Estado de cuenta',
  role_assigned: 'Rol asignado',
  role_deactivated: 'Rol desactivado',
  role_removed: 'Rol eliminado',
  payment_status_updated: 'Estado de pago',
  calculation_deleted: 'Cálculo borrado',
}

const ROLE_LABELS = {
  admin: 'Admin',
  user: 'Usuario',
}

const ROLE_AUDIT_ACTIONS = new Set([
  'role_assigned',
  'role_deactivated',
  'role_removed',
])

const copyText = async (value) => {
  if (!value || !navigator.clipboard?.writeText) return
  try {
    await navigator.clipboard.writeText(value)
  } catch {
    // ignore
  }
}

const whenLabel = (value) => {
  if (!value) return '—'
  const relative = formatRelativeFromNow(value)
  const absolute = formatDateTime(value)
  return relative && relative !== absolute ? `${relative} · ${absolute}` : absolute
}

const methodShort = (method) => {
  const normalized = normalizePensionMethod(method)
  if (normalized === METHOD.FIXED_AMOUNT) return 'Monto fijo'
  if (normalized === METHOD.IMM) return '% IMM'
  return 'UTM'
}

const factorFromConfig = (row) => {
  if (!row) return '—'
  const method = normalizePensionMethod(row.method)
  if (method === METHOD.IMM) {
    return row.percentage != null ? `${row.percentage}% IMM` : '—'
  }
  if (method === METHOD.FIXED_AMOUNT) {
    return row.amount != null ? String(row.amount) : '—'
  }
  return row.percentage != null ? `${row.percentage} UTM` : '—'
}

const emptyValues = () => ({
  [METHOD.UTM]: '',
  [METHOD.IMM]: '',
  [METHOD.FIXED_AMOUNT]: '',
})

const valueFieldCopy = {
  [METHOD.UTM]: {
    label: 'UTM de la resolución',
    placeholder: 'Ej. 2,05',
  },
  [METHOD.IMM]: {
    label: '% del sueldo mínimo',
    placeholder: 'Ej. 40',
  },
  [METHOD.FIXED_AMOUNT]: {
    label: 'Monto en pesos',
    placeholder: 'Ej. 6000',
  },
}

const onboardingLabel = (status) => {
  if (status === 'completed') return 'Completado'
  if (status === 'skipped') return 'Omitido'
  return 'Pendiente'
}

const roleIdFromAudit = (row) =>
  row.after?.role_id || row.before?.role_id || null

const auditDetail = (row, rolesById) => {
  if (ROLE_AUDIT_ACTIONS.has(row.action)) {
    const roleId = roleIdFromAudit(row)
    const name = roleId ? rolesById.get(String(roleId)) : null
    if (name) return ROLE_LABELS[name] || name
    return null
  }

  const beforeStatus = row.before?.status
  const afterStatus = row.after?.status
  if (beforeStatus && afterStatus && beforeStatus !== afterStatus) {
    return `${beforeStatus} → ${afterStatus}`
  }
  return null
}

const valuesFromUser = (user) => {
  const next = emptyValues()
  const method = user.pensionMethod
  if (method && user.pensionValueRaw != null && user.pensionValueRaw !== '') {
    next[method] = String(user.pensionValueRaw)
  }
  return next
}

const UserPensionConfigForm = ({ user, onSave }) => {
  const storedMethod = user.pensionMethod || METHOD.UTM
  const [method, setMethod] = useState(storedMethod)
  const [values, setValues] = useState(() => valuesFromUser(user))
  const [valueError, setValueError] = useState('')
  const [submitError, setSubmitError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setMethod(user.pensionMethod || METHOD.UTM)
    setValues(valuesFromUser(user))
    setValueError('')
    setSubmitError(null)
  }, [user])

  const currentValue = values[method] ?? ''
  const storedValue =
    user.pensionMethod && user.pensionValueRaw != null
      ? String(user.pensionValueRaw)
      : ''
  const dirty =
    method !== storedMethod || String(currentValue).trim() !== storedValue

  const handleValueChange = (event) => {
    const nextValue = event.target.value
    setValues((prev) => ({ ...prev, [method]: nextValue }))
    setValueError('')
  }

  const handleSave = async () => {
    const amount = method === METHOD.IMM ? null : currentValue
    const percentage = method === METHOD.IMM ? currentValue : null
    const validation = validatePensionValue(method, percentage, amount, {
      allowFixedAmount: true,
    })
    if (!validation.valid) {
      setValueError(validation.error)
      return
    }

    setSaving(true)
    setSubmitError(null)
    setValueError('')
    try {
      await onSave({
        method,
        amount,
        percentage,
        paymentDay: user.paymentDay,
      })
    } catch (err) {
      setSubmitError(err?.message || 'No pudimos guardar la configuración.')
    } finally {
      setSaving(false)
    }
  }

  const field = valueFieldCopy[method]

  return (
    <S.ConfigEditor>
      <S.MetaItem>
        <S.MethodChips role="radiogroup" aria-label="Método de pensión">
          {ADMIN_CONFIGURABLE_METHODS.map((value) => (
            <S.MethodChip
              key={value}
              type="button"
              role="radio"
              aria-checked={method === value}
              $active={method === value}
              disabled={saving}
              onClick={() => {
                setMethod(value)
                setValueError('')
              }}
            >
              {methodShort(value)}
            </S.MethodChip>
          ))}
        </S.MethodChips>
      </S.MetaItem>

      <S.MetaItem>
        <S.MetaLabel>{field.label}</S.MetaLabel>
        <S.ConfigInputWrap>
          <Input
            name="adminPensionValue"
            type="text"
            inputMode="decimal"
            placeholder={field.placeholder}
            value={currentValue}
            onChange={handleValueChange}
            disabled={saving}
          />
        </S.ConfigInputWrap>
        {valueError && <S.ConfigError>{valueError}</S.ConfigError>}
      </S.MetaItem>

      <S.FormActions>
        <Button
          type="button"
          disabled={!dirty || saving}
          loading={saving}
          onClick={handleSave}
        >
          Guardar
        </Button>
      </S.FormActions>

      {submitError && <S.ErrorText>{submitError}</S.ErrorText>}
      <S.HintText>
        El día de pago se conserva. Si el mes sigue sin pagar, el monto se
        actualiza con esta fórmula; si ya está pagado, se usa el próximo mes.
      </S.HintText>
    </S.ConfigEditor>
  )
}

export const UserAccountMeta = ({ user, account }) => {
  const providers = (account?.providers || []).map(
    (provider) => PROVIDER_LABELS[provider] || provider
  )
  const lastAccess = account?.lastSeenAt || account?.lastSignInAt

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Cuenta</S.SectionTitle>
      </S.SectionHeader>

      <S.MetaGrid $columns={2}>
        <S.MetaItem>
          <S.MetaLabel>Email</S.MetaLabel>
          <S.MetaValue $muted={!user.email}>
            {user.email || 'Sin email'}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>WhatsApp</S.MetaLabel>
          <S.MetaValue $muted={!user.whatsapp}>
            {user.whatsapp || 'Sin teléfono'}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>Onboarding</S.MetaLabel>
          <S.MetaValue>
            {onboardingLabel(user.onboardingStatus)}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>Proveedor</S.MetaLabel>
          <S.MetaValue $muted={!providers.length}>
            {providers.length ? providers.join(', ') : '—'}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>ID</S.MetaLabel>
          <S.MonoValue
            type="button"
            title="Copiar ID"
            onClick={() => copyText(user.id)}
          >
            {user.id}
          </S.MonoValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>Alta</S.MetaLabel>
          <S.MetaValue $muted={!user.createdAtRaw}>
            {user.createdAtRaw ? whenLabel(user.createdAtRaw) : user.createdAt || '—'}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>Último acceso</S.MetaLabel>
          <S.MetaValue $muted={!lastAccess}>
            {lastAccess ? whenLabel(lastAccess) : 'Sin sesiones'}
          </S.MetaValue>
        </S.MetaItem>
        <S.MetaItem>
          <S.MetaLabel>Último login</S.MetaLabel>
          <S.MetaValue $muted={!account?.lastSignInAt}>
            {account?.lastSignInAt ? whenLabel(account.lastSignInAt) : '—'}
          </S.MetaValue>
        </S.MetaItem>
      </S.MetaGrid>
      <S.HintText>
        Onboarding es el flujo inicial en la app (completado, omitido o
        pendiente). Setup completo es que ya hay método y monto de pensión;
        se puede dejar listo aquí aunque el onboarding siga pendiente.
      </S.HintText>
      <S.HintText>
        El último acceso usa el refresh de sesión; el último login es el OAuth.
      </S.HintText>
    </S.DetailSection>
  )
}

export const UserPensionConfig = ({ user, onSave }) => (
  <S.DetailSection>
    <S.SectionHeader>
      <S.SectionTitle>Método de pensión</S.SectionTitle>
    </S.SectionHeader>

    <S.MetaGrid $columns={2}>
      <S.MetaItem>
        <S.MetaLabel>Día de pago</S.MetaLabel>
        <S.MetaValue $muted={user.paymentDay == null}>
          {user.paymentDay != null ? `Día ${user.paymentDay}` : 'Sin configurar'}
        </S.MetaValue>
      </S.MetaItem>
      <S.MetaItem>
        <S.MetaLabel>Config actualizada</S.MetaLabel>
        <S.MetaValue $muted={!user.configUpdatedAt}>
          {user.configUpdatedAt ? whenLabel(user.configUpdatedAt) : '—'}
        </S.MetaValue>
      </S.MetaItem>
    </S.MetaGrid>

    <UserPensionConfigForm user={user} onSave={onSave} />
  </S.DetailSection>
)

export const UserSessions = ({ userId }) => {
  const [sessions, setSessions] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [revokingId, setRevokingId] = useState(null)
  const [confirmId, setConfirmId] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [sessionRows, eventRows] = await Promise.all([
        getUserOpsSessions(userId),
        getUserOpsAuthEvents(userId),
      ])
      setSessions(sessionRows)
      setEvents(eventRows)
    } catch (err) {
      setError(err?.message || 'No pudimos cargar las sesiones.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([getUserOpsSessions(userId), getUserOpsAuthEvents(userId)])
      .then(([sessionRows, eventRows]) => {
        if (cancelled) return
        setSessions(sessionRows)
        setEvents(eventRows)
        setError(null)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'No pudimos cargar las sesiones.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  const handleRevoke = async (sessionId) => {
    setRevokingId(sessionId)
    try {
      await revokeUserOpsSession(sessionId)
      setConfirmId(null)
      await load()
    } catch (err) {
      const code = err?.code || err?.message || ''
      setError(
        String(code).includes('cannot_revoke_current_session')
          ? 'No puedes cerrar tu sesión actual desde aquí.'
          : err?.message || 'No pudimos cerrar la sesión.'
      )
    } finally {
      setRevokingId(null)
    }
  }

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Sesiones</S.SectionTitle>
        {!loading && !error && (
          <S.SectionHint>
            {sessions.length} activa{sessions.length === 1 ? '' : 's'}
          </S.SectionHint>
        )}
      </S.SectionHeader>

      {loading && <Spinner />}
      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}

      {!loading && !error && sessions.length === 0 && (
        <S.EmptyState>
          <strong>Sin sesiones activas</strong>
          <span>No hay dispositivos conectados ahora.</span>
        </S.EmptyState>
      )}

      {!loading && sessions.length > 0 && (
        <S.OpsList>
          {sessions.map((session) => {
            const device = parseUserAgent(session.userAgent)
            return (
              <S.OpsRow key={session.id}>
                <S.OpsMain>
                  <S.OpsTitle>{device.label}</S.OpsTitle>
                  <S.OpsMeta>
                    {session.ip || 'IP desconocida'}
                    {' · desde '}
                    {whenLabel(session.createdAt)}
                  </S.OpsMeta>
                  <S.OpsMeta>
                    Último uso: {whenLabel(session.lastActiveAt)}
                  </S.OpsMeta>
                </S.OpsMain>
                {confirmId === session.id ? (
                  <S.StatusToggle>
                    <S.DangerButton
                      type="button"
                      disabled={revokingId === session.id}
                      onClick={() => handleRevoke(session.id)}
                    >
                      Confirmar
                    </S.DangerButton>
                    <S.TextButton
                      type="button"
                      onClick={() => setConfirmId(null)}
                    >
                      Cancelar
                    </S.TextButton>
                  </S.StatusToggle>
                ) : (
                  <S.DangerButton
                    type="button"
                    onClick={() => setConfirmId(session.id)}
                  >
                    Cerrar
                  </S.DangerButton>
                )}
              </S.OpsRow>
            )
          })}
        </S.OpsList>
      )}

      {!loading && events.length > 0 && (
        <>
          <S.MetaLabel>Eventos de acceso</S.MetaLabel>
          <S.OpsList>
            {events.map((event) => (
              <S.OpsRow key={event.id}>
                <S.OpsMain>
                  <S.OpsTitle>
                    {AUTH_ACTION_LABELS[event.action] || event.action}
                  </S.OpsTitle>
                  <S.OpsMeta>
                    {whenLabel(event.createdAt)}
                    {event.provider
                      ? ` · ${PROVIDER_LABELS[event.provider] || event.provider}`
                      : ''}
                    {event.ip ? ` · ${event.ip}` : ''}
                  </S.OpsMeta>
                </S.OpsMain>
              </S.OpsRow>
            ))}
          </S.OpsList>
        </>
      )}
    </S.DetailSection>
  )
}

export const UserConfigHistory = ({ userId, refreshKey = 0 }) => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getUserConfigHistory(userId)
      .then((data) => {
        if (!cancelled) {
          setRows(data)
          setError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'No pudimos cargar el historial.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, refreshKey])

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Historial de configuración</S.SectionTitle>
        {!loading && !error && (
          <S.SectionHint>
            {rows.length} cambio{rows.length === 1 ? '' : 's'}
          </S.SectionHint>
        )}
      </S.SectionHeader>

      {loading && <Spinner />}
      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}
      {!loading && !error && rows.length === 0 && (
        <S.HintText>Sin cambios registrados.</S.HintText>
      )}

      {!loading && rows.length > 0 && (
        <S.OpsList>
          {rows.map((row) => {
            const after = row.after || {}
            const before = row.before || {}
            const afterLabel = `${methodShort(after.method)} · ${factorFromConfig(after)}`
            const beforeLabel = row.before
              ? `${methodShort(before.method)} · ${factorFromConfig(before)}`
              : null
            return (
              <S.OpsRow key={row.id}>
                <S.OpsMain>
                  <S.OpsTitle>
                    {row.changeType === 'insert' ? 'Configuración inicial' : afterLabel}
                  </S.OpsTitle>
                  {beforeLabel && row.changeType === 'update' && (
                    <S.OpsMeta>
                      Antes: {beforeLabel}
                    </S.OpsMeta>
                  )}
                  <S.OpsMeta>
                    {whenLabel(row.createdAt)}
                    {row.changedByName ? ` · ${row.changedByName}` : ''}
                    {after.payment_day != null ? ` · día ${after.payment_day}` : ''}
                  </S.OpsMeta>
                </S.OpsMain>
              </S.OpsRow>
            )
          })}
        </S.OpsList>
      )}
    </S.DetailSection>
  )
}

export const UserSupportContext = ({ userId, userName }) => {
  const [tickets, setTickets] = useState([])
  const [notifications, setNotifications] = useState([])
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([
      getUserOpsTickets(userId),
      getUserOpsNotifications(userId),
      getUserOpsLogs({ userId, name: userName }),
    ])
      .then(([ticketRows, notifRows, logRows]) => {
        if (cancelled) return
        setTickets(ticketRows)
        setNotifications(notifRows)
        setLogs(logRows)
        setError(null)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'No pudimos cargar soporte y avisos.')
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, userName])

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Soporte y avisos</S.SectionTitle>
      </S.SectionHeader>

      {loading && <Spinner />}
      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}

      {!loading && (
        <>
          <S.MetaLabel>Tickets</S.MetaLabel>
          {tickets.length === 0 ? (
            <S.HintText>Sin tickets.</S.HintText>
          ) : (
            <S.OpsList>
              {tickets.map((ticket) => (
                <S.OpsRow key={ticket.id}>
                  <S.OpsMain>
                    <S.OpsTitle>
                      {ticket.tracking_code || 'Ticket'} ·{' '}
                      {SUPPORT_CATEGORY_LABELS[ticket.category] || ticket.category}
                    </S.OpsTitle>
                    <S.OpsMeta>
                      {ticket.status === 'open' ? 'Abierto' : 'Cerrado'}
                      {' · '}
                      {whenLabel(ticket.created_at)}
                    </S.OpsMeta>
                    <S.OpsMeta>{ticket.message}</S.OpsMeta>
                  </S.OpsMain>
                </S.OpsRow>
              ))}
            </S.OpsList>
          )}

          <S.MetaLabel>Notificaciones</S.MetaLabel>
          {notifications.length === 0 ? (
            <S.HintText>Sin notificaciones.</S.HintText>
          ) : (
            <S.OpsList>
              {notifications.map((item) => (
                <S.OpsRow key={item.id}>
                  <S.OpsMain>
                    <S.OpsTitle>{item.title || item.type}</S.OpsTitle>
                    <S.OpsMeta>
                      {item.status === 'read' ? 'Leída' : 'No leída'}
                      {item.email_status ? ` · mail ${item.email_status}` : ''}
                      {' · '}
                      {whenLabel(item.created_at)}
                    </S.OpsMeta>
                  </S.OpsMain>
                </S.OpsRow>
              ))}
            </S.OpsList>
          )}

          <S.MetaLabel>Logs del notifier</S.MetaLabel>
          {logs.length === 0 ? (
            <S.HintText>
              Sin coincidencias en bitácora (el sender a menudo no guarda user_id).
            </S.HintText>
          ) : (
            <S.OpsList>
              {logs.map((row) => (
                <S.OpsRow key={row.id}>
                  <S.OpsMain>
                    <S.OpsTitle>
                      {row.context} · {row.level}
                    </S.OpsTitle>
                    <S.OpsMeta>{row.message}</S.OpsMeta>
                    <S.OpsMeta>{whenLabel(row.createdAtRaw)}</S.OpsMeta>
                  </S.OpsMain>
                </S.OpsRow>
              ))}
            </S.OpsList>
          )}
        </>
      )}
    </S.DetailSection>
  )
}

export const UserAuditLog = ({ userId }) => {
  const [rows, setRows] = useState([])
  const [rolesById, setRolesById] = useState(() => new Map())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.allSettled([getUserAuditEvents(userId), getRolesCatalog()])
      .then(([eventsResult, catalogResult]) => {
        if (cancelled) return
        if (eventsResult.status === 'rejected') {
          setError(
            eventsResult.reason?.message || 'No pudimos cargar la bitácora.'
          )
          return
        }
        const catalog =
          catalogResult.status === 'fulfilled' ? catalogResult.value : []
        setRolesById(
          new Map((catalog || []).map((role) => [String(role.id), role.name]))
        )
        setRows(eventsResult.value)
        setError(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId])

  return (
    <S.DetailSection>
      <S.SectionHeader>
        <S.SectionTitle>Acciones de operador</S.SectionTitle>
        {!loading && !error && (
          <S.SectionHint>
            {rows.length} evento{rows.length === 1 ? '' : 's'}
          </S.SectionHint>
        )}
      </S.SectionHeader>

      {loading && <Spinner />}
      {!loading && error && <S.ErrorText>{error}</S.ErrorText>}
      {!loading && !error && rows.length === 0 && (
        <S.HintText>Todavía no hay acciones registradas sobre esta cuenta.</S.HintText>
      )}

      {!loading && rows.length > 0 && (
        <S.OpsList>
          {rows.map((row) => {
            const detail = auditDetail(row, rolesById)
            return (
              <S.OpsRow key={row.id}>
                <S.OpsMain>
                  <S.OpsTitle>
                    {AUDIT_ACTION_LABELS[row.action] || row.action}
                  </S.OpsTitle>
                  <S.OpsMeta>
                    {whenLabel(row.createdAt)}
                    {row.actorName ? ` · ${row.actorName}` : ''}
                    {detail ? ` · ${detail}` : ''}
                  </S.OpsMeta>
                </S.OpsMain>
              </S.OpsRow>
            )
          })}
        </S.OpsList>
      )}
    </S.DetailSection>
  )
}
